/**
 * The one caller of Cloud Functions callables.
 *
 * Every callable goes through `callFunction(name, data, options)` (or a
 * `callableRef` built on it) so that
 *   1. each request carries `__client: { build, level }` — the server's
 *      `onCallCompat` strips it and refuses a client below the live
 *      compatibility level, and
 *   2. a refusal ("client-outdated") raises the update modal in one place
 *      instead of being handled (or forgotten) at ~50 call sites.
 *
 * See "The gate on every callable" in docs/spec/19d-client-backend-compat.md.
 *
 * `firebase/functions` is imported dynamically on purpose: a static import
 * would pull the whole Functions SDK into the entry bundle. A source gate
 * (tests/gates/callableGate.test.js) keeps both `httpsCallable` and any
 * `firebase/functions` import out of every other file.
 */
import { COMPAT_LEVEL } from "@sendly/shared";
import { buildId } from "../buildInfo";
import { USE_EMULATORS, EMULATOR_HOST, EMULATOR_PORTS } from "../emulators";

/** Window event the compat hook listens to; `detail.liveLevel` is the server's level. */
export const CLIENT_OUTDATED_EVENT = "sendly:client-outdated";

/** The machine code the server answers an outdated client with (`details.code`). */
export const CLIENT_OUTDATED_CODE = "client-outdated";

const CALLABLE_REGION = "europe-west1";

let sdkPromise = null;
let emulatorConnected = false;

/** The Functions SDK, imported once. A failed import (chunk load error) is not cached. */
function loadSdk() {
  if (!sdkPromise) {
    sdkPromise = import("firebase/functions").catch((err) => {
      sdkPromise = null;
      throw err;
    });
  }
  return sdkPromise;
}

/**
 * The region's Functions instance. `getFunctions` returns the same instance on every
 * call; under the browser E2E run (`.env.e2e`, see ../emulators.js) it is pointed at
 * the local emulator once, before its first request.
 */
function functionsInstance(sdk) {
  const functions = sdk.getFunctions(undefined, CALLABLE_REGION);
  if (USE_EMULATORS && !emulatorConnected) {
    sdk.connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
    emulatorConnected = true;
  }
  return functions;
}

/** The compat stamp every request carries. */
export function clientStamp() {
  return { build: buildId, level: COMPAT_LEVEL };
}

/** Tell the app this bundle is below the live compat level. Never throws. */
export function announceClientOutdated(liveLevel) {
  try {
    window.dispatchEvent(new CustomEvent(CLIENT_OUTDATED_EVENT, { detail: { liveLevel } }));
  } catch {
    // No window (a non-browser runtime): nothing to notify, the caller still gets the rejection.
  }
}

function withClientStamp(data) {
  if (data === null || data === undefined) return { __client: clientStamp() };
  // Every callable takes an object payload; an array or a primitive has no room
  // for the stamp and would reach the server unchecked.
  if (typeof data !== "object" || Array.isArray(data)) {
    throw new TypeError("callFunction: the payload must be an object (or null/undefined)");
  }
  return { ...data, __client: clientStamp() };
}

/**
 * Call a callable.
 *
 * @param {string} name callable name (region europe-west1)
 * @param {object|null} [data] payload; `__client` is added to a copy
 * @param {import("firebase/functions").HttpsCallableOptions} [options] e.g. `{ timeout }`, passed through
 * @returns {Promise<{ data: any }>} the same result object as an `HttpsCallable`
 * @throws the SDK error unchanged; for an outdated client it is also marked
 *   `err.clientOutdated = true` (`err.details.code === "client-outdated"`),
 *   after `sendly:client-outdated` was dispatched. The promise always settles.
 */
export async function callFunction(name, data, options) {
  const sdk = await loadSdk();
  const callable = sdk.httpsCallable(functionsInstance(sdk), name, options);
  try {
    return await callable(withClientStamp(data));
  } catch (err) {
    if (err && err.details && err.details.code === CLIENT_OUTDATED_CODE) {
      err.clientOutdated = true;
      announceClientOutdated(err.details.liveLevel);
    }
    throw err;
  }
}

/**
 * A callable reference for callers that build it first and call it later (or
 * hand it on): `(data) => callFunction(name, data, options)`.
 */
export function callableRef(name, options) {
  return (data) => callFunction(name, data, options);
}
